import { useState, useEffect } from 'react';
import { Database, Download, CloudUpload, Loader2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';

const fmtSize = (b) => (b ? `${(b / 1048576).toFixed(1)} MB` : '-');
const fmtDate = (iso) => new Date(iso).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export const BackupPanel = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [running, setRunning] = useState(false);
  const [rowDownloading, setRowDownloading] = useState(null);

  const fetchBackups = async () => {
    try {
      const res = await api.get('/admin/backups');
      setBackups(res.data || []);
    } catch (e) { /* stil */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBackups(); }, []);

  const saveBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/zip' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadFresh = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/admin/backups/download', { responseType: 'blob', timeout: 120000 });
      saveBlob(res.data, `crm-backup-${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success('Backup gedownload');
    } catch (e) {
      toast.error('Kon backup niet downloaden');
    } finally { setDownloading(false); }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await api.post('/admin/backups/run');
      toast.success('Backup gestart — verschijnt binnen enkele seconden in de lijst');
      setTimeout(fetchBackups, 5000);
    } catch (e) {
      toast.error('Kon backup niet starten');
    } finally { setRunning(false); }
  };

  const handleDownloadStored = async (b) => {
    setRowDownloading(b.id);
    try {
      const res = await api.get(`/admin/backups/${b.id}/download`, { responseType: 'blob', timeout: 120000 });
      saveBlob(res.data, b.r2_key.split('/').pop());
    } catch (e) {
      toast.error('Kon backup niet downloaden');
    } finally { setRowDownloading(null); }
  };

  return (
    <Card data-testid="backup-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5" />
          Backups
        </CardTitle>
        <CardDescription>
          Elke nacht om 03:00 wordt automatisch een volledige database-backup naar de beveiligde cloudopslag (R2) geschreven. Backups worden 30 dagen bewaard.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleDownloadFresh} disabled={downloading} data-testid="backup-download-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Backup nu downloaden
          </Button>
          <Button variant="outline" onClick={handleRunNow} disabled={running} data-testid="backup-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CloudUpload className="w-4 h-4 mr-2" />}
            Nu backup naar cloud maken
          </Button>
          <Button variant="ghost" size="icon" onClick={fetchBackups} title="Vernieuwen" data-testid="backup-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Laden...</p>
        ) : backups.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="backup-empty">Nog geen automatische backups — de eerste draait vannacht om 03:00.</p>
        ) : (
          <div className="border rounded-lg divide-y divide-border/50 max-h-72 overflow-y-auto" data-testid="backup-list">
            {backups.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  {b.status === 'success'
                    ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                  <span className="font-medium">{fmtDate(b.created_at)}</span>
                  <Badge variant="outline" className="text-xs">{b.trigger === 'cron' ? 'automatisch' : 'handmatig'}</Badge>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {b.status === 'success' ? (
                    <>
                      <span className="text-muted-foreground text-xs">{fmtSize(b.size_bytes)} · {b.total_documents} records</span>
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadStored(b)} disabled={rowDownloading === b.id} data-testid={`backup-download-${b.id}`}>
                        {rowDownloading === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>
                    </>
                  ) : (
                    <span className="text-red-500 text-xs">Mislukt</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Tip: download af en toe een backup naar je eigen computer voor een extra kopie buiten de cloud. Codebackups maak je via de "Save to GitHub" knop in Emergent.
        </p>
      </CardContent>
    </Card>
  );
};

export default BackupPanel;
